import React from 'react';
import { Grid3X3, Settings, Users, Eye, Home, Shield, Database, FileText, BarChart3, Calendar, Mail, Bell, Lock, Key, CreditCard, Package, Truck, MapPin, Phone, Building, UserCheck, UserX, Folder, File, Search, Filter, Download, Upload, Edit, Trash2, Plus, Minus } from 'lucide-react';
import type { Module } from '../../services/security/modules.service';

interface ModulesTableSimpleProps {
  modules: Module[];
  isLoading: boolean;
  onViewModule: (module: Module) => void;
}

export const ModulesTableSimple: React.FC<ModulesTableSimpleProps> = ({
  modules,
  isLoading,
  onViewModule
}) => {

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (activo: boolean) => {
    return activo 
      ? 'bg-green-100 text-green-800 border-green-200' 
      : 'bg-red-100 text-red-800 border-red-200';
  };

  const getStatusText = (activo: boolean) => {
    return activo ? 'Activo' : 'Inactivo';
  };

  const getTypeText = (tipo: string) => {
    return tipo === 'navigation' ? 'Navegación' : 'Modal';
  };

  const getTypeColor = (tipo: string) => {
    return tipo === 'navigation'
      ? 'bg-blue-100 text-blue-800 border-blue-200'
      : 'bg-purple-100 text-purple-800 border-purple-200';
  };

  const getModuleIcon = (iconName?: string) => {
    const icons: { [key: string]: React.ComponentType<any> } = {
      'settings': Settings,
      'users': Users,
      'grid3x3': Grid3X3,
      'eye': Eye,
      'home': Home,
      'shield': Shield,
      'database': Database,
      'filetext': FileText,
      'barchart3': BarChart3,
      'calendar': Calendar,
      'mail': Mail,
      'bell': Bell,
      'lock': Lock,
      'key': Key,
      'creditcard': CreditCard,
      'package': Package,
      'truck': Truck,
      'mappin': MapPin,
      'phone': Phone,
      'building': Building,
      'usercheck': UserCheck,
      'userx': UserX,
      'folder': Folder,
      'file': File,
      'search': Search,
      'filter': Filter,
      'download': Download,
      'upload': Upload,
      'edit': Edit,
      'trash2': Trash2,
      'plus': Plus,
      'minus': Minus
    };
    
    return icons[iconName || 'grid3x3'] || Grid3X3;
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-500">Cargando módulos...</span>
      </div>
    );
  }

  if (modules.length === 0) {
    return (
      <div className="text-center py-12">
        <Grid3X3 className="mx-auto h-16 w-16 text-gray-400 mb-4" />
        <h3 className="text-xs font-medium text-gray-900 mb-2">
          No se encontraron módulos
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          No hay módulos que coincidan con los criterios de búsqueda
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">
              Módulo
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
              Tipo
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
              Estado
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
              Última Actualización
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {modules.map((module) => (
            <tr
              key={module.id}
              className="hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => onViewModule(module)}
            >
              <td className="px-6 py-5 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center">
                      {(() => {
                        const IconComponent = getModuleIcon(module.icono);
                        return <IconComponent className="h-5 w-5 text-white" />;
                      })()}
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-xs font-medium text-gray-900">
                      {module.nombre}
                    </div>
                    {module.descripcion && (
                      <div className="text-xs text-gray-500 truncate max-w-xs">
                        {module.descripcion}
                      </div>
                    )}
                    {module.ruta && (
                      <div className="text-xs text-blue-600 font-mono">
                        {module.ruta}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-5 whitespace-nowrap">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeColor(module.tipo)}`}>
                  {getTypeText(module.tipo)}
                </span>
              </td>
              <td className="px-6 py-5 whitespace-nowrap">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(module.activo)}`}>
                  {getStatusText(module.activo)}
                </span>
              </td>
              <td className="px-6 py-5 whitespace-nowrap text-xs text-gray-500">
                {formatDate(module.actualizadoEn)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};